import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {FaBan, FaEye, FaPaperclip} from "react-icons/fa";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {MaterialMovementsContext} from "../material-movements.context.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import projectUtils, {clearRequiredFieldError, markRequiredFields} from "../../../../../utils/project-utils.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useQuery} from "@tanstack/react-query";
import materialMovementService from "../../../../../services/web/material-movement-service.js";
import DevolutionCellRenderer from "./devolution-cell-renderer.jsx";
import ObservationCellRenderer from "./observation-cell-renderer.jsx";
import {toast} from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import DecreaseTermCellRenderer from "./decrease-term-cell-tenderer.jsx";

const MySwal = withReactContent(Swal);
let openModalObservation = false, openModalDecreaseTerm = false

const Decrease = ({storageSelected}) => {
    const {userSelected, refetchCautions, setIsLoading} = useContext(MaterialMovementsContext)
    const [openModal, setOpenModal] = useState(false)
    const [cautions, setCautions] = useState([])
    const [password, setPassword] = useState("")
    const decreaseSelectionRef = useRef(null)
    const saveButtonRef = useRef(null)
    const passwordInputRef = useRef(null)
    const hasSelectedDecrease = cautions.some((caution) => Number(caution.quantityReturn) > 0)

    const {data: dataCautions, fetchStatus, isFetching: isLoadingCaution} = useQuery({
        queryKey: ['cautions-decrease', userSelected],
        queryFn: () => materialMovementService.getByUserAndStorage(userSelected.value, storageSelected.value),
        enabled: !!openModal
    });

    useEffect(() => {
        if (fetchStatus === "idle" && dataCautions) {
            setCautions(dataCautions)
        }
    }, [dataCautions, fetchStatus]);

    useEffect(() => {
        setIsLoading(isLoadingCaution)
    }, [isLoadingCaution]);

    useEffect(() => {
        if (!openModal) {
            setPassword("")
            setCautions([])
        }
    }, [openModal]);

    useEffect(() => {
        clearRequiredFieldError(decreaseSelectionRef.current)
    }, [hasSelectedDecrease]);

    const showModal = () => {
        if (!userSelected) {
            projectUtils.swalMessage('warning', '', `Selecione um solicitante.`, 'Ok');
            return;
        }

        setOpenModal(true)
    }

    const canAttachDecreaseTerm = (material) => {
        const materialData = Object.prototype.hasOwnProperty.call(material ?? {}, "decrease_term") ? material : material?.material;
        const hasDecreaseTermKey = Object.prototype.hasOwnProperty.call(materialData ?? {}, "decrease_term");
        const enabledValues = [true, 1, "1", "true"];

        return hasDecreaseTermKey && enabledValues.includes(materialData?.decrease_term);
    }

    const columnDefs = [
        {headerName: "Entrega", field: "date", width: 140},
        {headerName: "Depósito", field: "storage_description", sortable: false},
        {headerName: "Material", field: "material_description", flex: 1, sortable: false},
        {headerName: "Quantidade", field: "quantity", sortable: false, width: 100, hozAlign: "center"},
        {headerName: "Observação", field: "", width: 130,
            cellRenderer: ObservationCellRenderer,
            cellRendererParams: {
                setCautions,
                label: "Observação de baixa",
                setOpenModalObservation: (value) => openModalObservation = value,
            }
        },
        {headerName: "Termo de baixa", field: "decrease_term", width: 120, sortable: false,
            cellRenderer: DecreaseTermCellRenderer,
            cellRendererParams: {
                setCautions,
                setOpenModalDecreaseTerm: (value) => openModalDecreaseTerm = value,
                canAttachDecreaseTerm
            }
        },
        {headerName: "Baixar *", field: "", width: 90,
            cellRenderer: DevolutionCellRenderer,
            cellRendererParams: {
                setCautions
            }
        },
    ]

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const save = (confirmationMessage) => {
        const invalidRequiredFields = markRequiredFields();
        if (invalidRequiredFields?.length) {
            projectUtils.swalErrorMessage('', 'Preencha os campos obrigatórios.', 'Ok');
            return;
        }

        if (!confirmationMessage) {
            MySwal.fire({
                icon: "warning",
                title: "Atenção",
                showCancelButton: true,
                html: `Esta operação não pode ser desfeita! <br>Para baixar o material, digite <b>excluir material do depósito</b> abaixo:`,
                confirmButtonText: "Confirmar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: '#063A6C',
                input: "text",
                reverseButtons: true,
            }).then(({isConfirmed, value}) => {
                if (isConfirmed) save(value);
            });
            return
        }

        setIsLoading(true);
        const currentPassword = passwordInputRef.current?.value ?? password;

        const formData = new FormData();

        const materialsDevolution = cautions.filter(c => c.quantityReturn && c.quantityReturn > 0).map(c => ({
            caution_id: c.id,
            movement_id: c.movement_id,
            observation_decrease: c.observation || "",
            quantity: c.quantityReturn,
        }))

        formData.append('requester', userSelected.value);
        formData.append('materials-decrease', JSON.stringify(materialsDevolution));
        formData.append('storage-selected', storageSelected.value);
        formData.append('password', currentPassword);
        formData.append('confirmation', confirmationMessage);

        cautions
            .filter(c => c.quantityReturn && c.quantityReturn > 0 && canAttachDecreaseTerm(c) && c.decrease_term_file)
            .forEach(c => formData.append(`decrease_term[${c.id}]`, c.decrease_term_file));

        materialMovementService.saveDecrease(formData)
            .then(() => {
                toast.success("Baixa efetuada com sucesso")
                refetchCautions()
                setTimeout(() => setOpenModal(false), 1000)
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    useGlobalKey("Enter", () => {
        if (openModal && !openModalObservation && !openModalDecreaseTerm) saveButtonRef.current?.click()
    })

    return (
        <>
            <button onClick={showModal} className={"w-[30%] m-2 flex items-center justify-center rounded-[5px] h-[70px] bg-[#ed5565] border-[#ed5565] text-white cursor-pointer hover:bg-[#EF818D]"}>
                <FaBan size={25}/>
                <span className={"ml-2 text-[18px] uppercase font-bold"}>Baixa</span>
            </button>

            <Modal alignTop={true} open={openModal} setOpen={setOpenModal} overflow={true} size="md" backdrop="static" keyboard={false} title={"Baixa"} className={"w-10/12"}>
                <Modal.Body className="p-2">
                    <div ref={decreaseSelectionRef} tabIndex={-1} data-required-field="true" data-required-has-value={hasSelectedDecrease ? "true" : "false"}
                        className="min-h-[74px] border border-transparent rounded-md transition-[border-color,border-width] duration-150 ease-out">

                        <AgGridReact
                            rowData={cautions}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            getRowId={params => params.data?.id}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                            domLayout='autoHeight'
                            onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                        />

                    </div>
                </Modal.Body>
                <Modal.Footer className={"p-2"}>
                    <div className={"w-full flex justify-end"}>
                        <div className={"flex gap-4"}>
                            <div className={"flex"}>
                                <div className={"min-w-[100px] bg-[#e0e0e0] border border-[#b2b2b2] h-9 p-2 -mr-px"}>
                                    <p><b>Solicitante:</b> {userSelected?.label}</p>
                                </div>
                                <InputForm
                                    required
                                    inputClass={"bg-white !h-9 !border-[#b2b2b2] !rounded-[0px] !w-[100px]"}
                                    label={"Senha/PIN *"}
                                    id={`password`}
                                    type={"password"}
                                    value={password}
                                    name={"password"}
                                    inputRef={passwordInputRef}
                                    onChange={({target: {value}}) => setPassword(value)}/>
                            </div>
                            <Button
                                reference={saveButtonRef}
                                target={"danger"}
                                onClick={() => save(null)}
                                actionFeedbackType="save"
                                loadingText="Baixando..."
                                successText="Baixa efetuada com sucesso"
                                className={"!h-9 bg-[#ed5565]"}
                            >
                                Baixar
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Decrease;